import { shallowEqual, useSelector } from "react-redux";
import { getDeep } from "../functions";
import { STAFF_USER_ROLES, TRAINER_USER_ROLES, VALID_USER_ROLES } from "../constants";

// NIBHV or "staff" roles:
const staffRoles = ["certification", "development-and-advice"];
// Trainer roles
const trainerRoles = ["trainer-contact"];

/**
 * Matches NIBHV numbers with or without dashes:
 * - 123-456-789
 * - 123456789
 * @type {RegExp}
 */
export const NIBHV_NUMBER_REGEX = /^(\d{3}-\d{3}-\d{3}|\d{9})$/;

export function isNIBHVNumber(number) {
    return NIBHV_NUMBER_REGEX.test(number);
}

/**
 * Returns whether the current user as any of the given roles
 * Reused existing logic, should be optimised.
 * @param {String[]} userRoles
 * @param roles
 * @returns {Boolean}
 */
export function hasAnyRole(userRoles, ...roles) {
    return !!userRoles.find((role) => roles.includes(role));
}

/**
 * Returns whether the current user has a role that allows them to use this app.
 * @param {String[]} userRoles
 * @returns {Boolean}
 */
export function hasValidRole(userRoles) {
    return hasAnyRole(userRoles, ...VALID_USER_ROLES);
}

/**
 * Returns whether the current user has a staff role.
 * @param {String[]} [userRoles] The user's roles. Will be retrieved if not provided.
 * @returns {Boolean}
 */
export function hasStaffRole(userRoles) {
    return hasAnyRole(userRoles, ...STAFF_USER_ROLES);
}

/**
 * Returns whether the current user has the trainer contact role.
 * @param {String[]} [userRoles] The user's roles. Will be retrieved if not provided.
 * @returns {Boolean}
 */
export function isTrainerContact(userRoles) {
    return hasAnyRole(userRoles, ...TRAINER_USER_ROLES);
}

/**
 * Returns the company authorization for the given trainer UUID.
 * @param {Object[]} companyAuthorizations
 * @param {String} trainerUUID
 * @returns {{is_trainer_contact: Boolean}|undefined}
 */
export function getTrainerAuthorization(companyAuthorizations, trainerUUID) {
    return companyAuthorizations.find((authorization) => authorization.company === trainerUUID);
}

/**
 * Returns whether the current user is a trainer contact of the trainer.
 * @param {{company_authorizations: Object[]}} user
 * @param {String} trainerUUID The trainer's UUID.
 * @returns {Boolean}
 */
export function isTrainerContactOf(user, trainerUUID) {
    const authorization = getTrainerAuthorization(user?.company_authorizations, trainerUUID);
    return authorization && authorization.is_trainer_contact;
}

/**
 * Hook that returns the data of the currently logged-in user.
 * @returns {Object}
 */
export function useCurrentUser() {
    return useSelector((state) => state.authentication?.user, shallowEqual);
}

/**
 * Returns the permissions of the current user.
 * @returns {Object}
 */
export function usePermissions() {
    return useSelector((state) => state.authentication?.user?.permissions, shallowEqual);
}

/**
 * Returns whether the current user has a specific permission.
 * Reused existing logic, should be optimised.
 * @param {Object} permissions User permissions object.
 * @param {string} object
 * @param {string} permission
 * @returns {boolean}
 */
export function hasPermission(permissions, object, permission) {
    return !!getDeep(permissions, object, permission);
}

/**
 * Analyzes the given user data and verifies if the user is allowed to use this app.
 * @param {{is_blocked: boolean, roles: string[]}} user
 * @returns {(boolean|string)[]|boolean[]}
 */
export function userMayLogin(user) {
    if (user.is_blocked) {
        return [false, "blocked"];
    }

    // Check if there is any other role than student or instructor:
    if (!hasValidRole(user?.roles)) {
        return [false, "invalid_role"];
    }
    return [true];
}
